import { GenericMessageDialog } from 'uicomponents';

import DialogService from './dialogs/DialogService';
import loggerService from './logger.service';

import i18n from '@/i18n';

export enum OS {
  MAC_OS = 'Mac OS',
  WINDOWS = 'Windows',
  ANDROID = 'Android',
  LINUX = 'Linux',
  IOS = 'iOS'
}

export const MAC_OS_NETWORK_SETTINGS_URL = 'x-apple.systempreferences:com.apple.preference.network';
export const WINDOWS_NETWORK_SETTINGS_URL = 'ms-settings:network';

export function getOS() {
  let isSupported = false;
  const userAgent = window.navigator.userAgent,
    platform = window.navigator.platform,
    macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
    windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
    iosPlatforms = ['iPhone', 'iPad', 'iPod'];

  let os = '';

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'Mac OS';
    isSupported = true;
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS';
    isSupported = true;
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
    isSupported = true;
  } else if (/Android/.test(userAgent)) {
    os = 'Android';
    isSupported = true;
  } else if (/Linux/.test(platform)) {
    os = 'Linux';
  }

  return {
    os,
    isSupported
  };
}

export function getBrowserName() {
  const browserName = getBrowserSpecs()?.name;
  const isBrowserSupported = ['Chrome', 'Edge'].includes(browserName);

  return {
    browserName,
    isBrowserSupported
  };
}

export function detectAdBlock() {
  const adBlockEnabled = checkAdBlocker(false);

  return {
    adBlockEnabled: adBlockEnabled
  };
}

export function isBrowserChrome() {
  return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
}

export async function testAudioOutputAvailability(showDialog = true): Promise<Boolean> {
  if (!isBrowserChrome()) {
    return true;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');

    if (audioOutputDevices.length === 0) {
      loggerService.info('No audio output devices found');
      if (showDialog) {
        DialogService.show(GenericMessageDialog, {
          title: i18n.global.t('@@translate@@ No audio output device detected'),
          message: i18n.global.t(
            "@@translate@@ Please connect your speakers/headphones and use the gear icon to test them if you're facing any issues."
          )
        });
      }
      return false;
    }
    loggerService.info('Audio output device detected');
    return true;
  } catch (error) {
    loggerService.info('Failed to check for audio output', error);
    return false;
  }
}

export function checkAdBlocker(showError = true): Boolean {
  const fakeAd = document.createElement('div');
  fakeAd.className = 'textads banner-ads banner_ads ad-unit ad-zone ad-space adsbox';

  fakeAd.style.height = '1px';
  fakeAd.style.position = 'fixed';
  fakeAd.style.top = '-100px';

  document.body.appendChild(fakeAd);

  const detected = fakeAd.offsetHeight === 0;

  if (detected && showError) {
    loggerService.info('Ad blocker Detected');
    DialogService.show(GenericMessageDialog, {
      title: i18n.global.t('@@translate@@ Please disable ad blocker for this page'),
      message: i18n.global.t(
        "@@translate@@ You seem to have an ad blocker enabled. It might prevent you from having the best possible experience. Please disable it. <br /><br / > If you need help on how to do this, click on the link below for further instructions: <br /> 'https://help.goldcast.io/troubleshooting/4408141111323-configuring-your-connection-settings-network-vpn-and-firewall?#whitelist-goldcast-in-ad-blocking-browser-extensions-12'"
      ),
      resolveLabel: i18n.global.t('@@translate@@ Yes, I understand')
    });
  } else {
    loggerService.info('Ad blocker not detected');
  }
  return detected;
}

export function getBrowserSpecs(): { name: string; version: string } {
  const userAgent = navigator.userAgent;
  let tem,
    version,
    browserName,
    M = userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

  M = M[2] ? [M[1], M[2]] : [(navigator as any)?.appName, (navigator as any)?.appVersion, '-?'];
  tem = userAgent.match(/version\/(\d+)/i);
  if (tem != null) {
    M.splice(1, 1, tem[1]);
  }
  version = M[1];

  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = 'Chrome';
    if (userAgent.match(/opr\//i)) {
      browserName = 'Opera';
    } else if (userAgent.match(/edg/i)) {
      browserName = 'Edge';
    }

    tem = userAgent.match(new RegExp(/\b(OPR|Edge|Edg)\/(\d+)/));
    if (tem) {
      version = tem[2];
    }
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = 'Firefox';
  } else if (userAgent.match(/EdgiOS/i)) {
    browserName = 'Edge';
  } else if (userAgent.match(/safari/i)) {
    browserName = 'Safari';
  } else {
    browserName = 'unknown';
  }

  return { name: browserName, version };
}

export function openSystemSoundSettings() {
  const os = getOS()?.os;

  if (os === 'Mac OS') {
    window.open('x-apple.systempreferences:com.apple.preference.sound', '_blank');
  } else if (os === 'Windows') {
    window.open('ms-settings:sound', '_blank');
  } else {
    loggerService.info("Won't open sound settings for os: ", os);
  }
}
export function getSystemNetworkSettings() {
  const os = getOS()?.os;

  if (os === OS.MAC_OS) {
    return MAC_OS_NETWORK_SETTINGS_URL;
  }
  if (os === OS.WINDOWS) {
    return WINDOWS_NETWORK_SETTINGS_URL;
  }
  return '';
}
